import type React from 'react'
import { useCallback, useMemo, useState } from 'react'

import IconCalendar from '@/assets/icons/icon-calendar.svg'
import IconDone from '@/assets/icons/icon-done-all.svg'
import IconFlag from '@/assets/icons/icon-flag.svg'
import IconListAdd from '@/assets/icons/icon-list-add.svg'
import IconList from '@/assets/icons/icon-list.svg'
import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import type { RepeatableTask } from '@/modules/tasks/service/repeatable-task-service'
import type { Task } from '@/modules/tasks/service/task-service'
import { parseTaskDate } from '@/modules/tasks/service/task-service'
import { classNames } from '@/utils/helpers'

export type NavigationEntry =
  | ({ type: 'task' } & Task)
  | ({ type: 'repeatable' } & RepeatableTask)

function isTaskDueToday(task: Task) {
  if (task.completed) {
    return false
  }
  const dueDate = parseTaskDate(task.dueDate)

  if (!dueDate) {
    return false
  }
  const today = new Date()

  return dueDate < today
}

function hasEnded(task: RepeatableTask) {
  const endDate = parseTaskDate(task.endDate)

  return endDate && endDate < new Date()
}

function isRepeatingTaskDueToday(task: RepeatableTask) {
  if (hasEnded(task)) {
    return false
  }
  const dueDate = parseTaskDate(task.next)

  if (!dueDate) {
    return false
  }
  const today = new Date()

  return dueDate < today
}

export function isDueToday(task: NavigationEntry) {
  return task.type === 'task'
    ? isTaskDueToday(task)
    : isRepeatingTaskDueToday(task)
}

export function isRecurring(task: NavigationEntry) {
  return task.type === 'repeatable'
}

export function isPlanned(task: NavigationEntry) {
  return task.type === 'task' && !task.completed && !isTaskDueToday(task)
}

export function isUncompleted(task: NavigationEntry) {
  return task.type === 'task' ? !task.completed : !hasEnded(task)
}

export function isDone(task: NavigationEntry) {
  return task.type === 'task' ? task.completed : hasEnded(task)
}

export function isAssigned(task: NavigationEntry) {
  return task.type === 'task' && !task.completed && task.assignees?.length !== 0
}

const items = {
  'due-today': {
    name: 'Due Today',
    icon: IconCalendar,
    match: isDueToday,
  },
  planned: {
    name: 'Planned',
    icon: IconFlag,
    match: isPlanned,
  },
  all: {
    name: 'All',
    icon: IconList,
    match: isUncompleted,
  },
  done: {
    name: 'Done',
    icon: IconDone,
    match: isDone,
  },
  assigned: {
    name: 'Assigned',
    icon: IconListAdd,
    match: isAssigned,
  },
} as const

interface NavigationProps {
  query?: string
  entries: NavigationEntry[]
  render: (
    task: NavigationEntry[],
    activeKey: keyof typeof items,
  ) => React.ReactNode
}

export const TaskNavigation = (props: NavigationProps) => {
  const { entries, render, query } = props
  const [filterKey, setFilterKey] = useState<keyof typeof items>('due-today')

  const filtered = useMemo(() => {
    const filter = items[filterKey] ?? items.all

    return entries
      .filter((task) => !query || task.name.toLowerCase().includes(query))
      .filter((task) => filter.match(task))
  }, [query, entries, filterKey])

  const filterCounts = useMemo(() => {
    return Object.fromEntries(
      Object.entries(items).map(([itemKey, item]) => {
        const count = entries.filter((task) => item.match(task)).length

        return [itemKey, count]
      }),
    )
  }, [entries])

  const content = useMemo(
    () => render(filtered, filterKey),
    [filterKey, filtered, render],
  )

  const renderEmpty = useCallback(() => {
    return (
      <EmptyTableContent className="col-span-12">
        It looks like you haven&apos;t created any tasks yet, click on the{' '}
        <span className="text-primary-500">New Task</span> button to do so.
      </EmptyTableContent>
    )
  }, [])

  const tabs = useMemo(
    () =>
      Object.entries(items).map(([itemKey, item], index) => (
        <button
          key={index}
          onClick={() => setFilterKey(itemKey as keyof typeof items)}
          className={classNames(
            'flex-1 group flex items-center justify-center h-[32px] px-[12px] gap-[8px] rounded-md cursor-pointer transition-all duration-300 ease-out',
            {
              'bg-primary-500 text-neutral-default': itemKey === filterKey,
              'text-title hover:text-title-500 hover:bg-primary-500/20 hover:text-primary-500':
                itemKey !== filterKey,
            },
          )}
        >
          <item.icon className="w-4 h-4" />
          <div className="text-sm font-medium truncate">{item.name}</div>
          <span
            className={classNames(
              'flex items-center justify-center text-xs font-medium min-w-[18px] p-[0.5px] px-[5px] rounded-full',
              {
                'text-neutral-default bg-neutral-default/20':
                  itemKey === filterKey,
                'text-gray-400 bg-[#E9E9ED] dark:bg-neutral-default/10 group-hover:bg-primary-500/20 group-hover:text-primary-500':
                  itemKey !== filterKey,
              },
            )}
          >
            {filterCounts[itemKey]}
          </span>
        </button>
      )),
    [filterCounts, filterKey],
  )

  return (
    <div className="grid gap-4">
      <div className="flex gap-[16px] justify-between flex-col-reverse md:flex-row">
        <div className="p-1 flex-1 grid grid-flow-row grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-6 2xl:grid-cols-6 3xl:grid-cols-6 rounded-lg border gap-[4px] items-center">
          {tabs}
        </div>
      </div>
      <EmptyProvider render={renderEmpty}>{content}</EmptyProvider>
    </div>
  )
}

export function useNavigationEntries(tasks: Task[]) {
  return useMemo<NavigationEntry[]>(() => {
    return tasks.map((task) => ({ type: 'task', ...task }) as const)
  }, [tasks])
}
